import moment from 'moment';
import { useMemo, useCallback, useRef, useEffect } from 'react';
import { List } from 'immutable';
import { Table, TableRowActionsCell } from 'altus-ui-components';
import { IconButton, Tooltip } from '@material-ui/core';
import withStyles from '@material-ui/styles/withStyles';
import EditIcon from '@material-ui/icons/Edit';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import DeleteIcon from '@material-ui/icons/Delete';
import PreviewIcon from '@material-ui/icons/Visibility';
import { EMPTY_LIST } from 'app/app.constants';
import { ATTACHMENTS_DATE_FORMAT } from 'features/settings/integrityManagement/helpers/integrityManagement.constants';

const IntegrityAttachmentsTable = ({
  attachments,
  allAttachments,
  setIsLoading,
  handlePreviewAttachment,
  handleUpdateAttachments,
}) => {

  const ensureList = (value) => (List.isList(value) ? value : List(value ?? []));

  const mapAttachmentForUpdate = (attachment) => ({
    created: attachment.reportedDate,
    fullName: attachment.fileName,
    extension: attachment.fileType,
    editDocumenturl: attachment.fileLink,
    fileSource: attachment.fileSource,
    externalId: attachment.externalFileId,
  });

  const tableItems = useMemo(
    () => ensureList(attachments ?? EMPTY_LIST),
    [attachments],
  );

  const attachmentsSourceList = useMemo(
    () => ensureList(allAttachments ?? attachments ?? EMPTY_LIST),
    [allAttachments, attachments],
  );

  const attachmentsSourceRef = useRef(attachmentsSourceList);

  useEffect(() => {
    attachmentsSourceRef.current = attachmentsSourceList;
  }, [attachmentsSourceList]);

  const handleDelete = useCallback(
    (row) => {
      const targetId = row?.original?.externalFileId;

      if (!targetId) return;

      const sourceItems = attachmentsSourceRef.current || EMPTY_LIST;
      const remainingAttachments = sourceItems.filter(
        (attachment) =>
          (attachment.externalFileId) !== targetId,
      );

      attachmentsSourceRef.current = remainingAttachments;

      handleUpdateAttachments(
        remainingAttachments.map(mapAttachmentForUpdate).toArray(),
        [],
        true,
      );
    },
    [handleUpdateAttachments],
  );

  const columns = useMemo(
    () => [
      {
        xs: 6,
        Header: 'Document',
        accessor: (attachment) => attachment.fileName,
      },
      {
        xs: 3,
        Header: 'Logged date',
        accessor: (attachment) =>
          moment(attachment.reportedDate).format(ATTACHMENTS_DATE_FORMAT),
      },
      {
        xs: 3,
        id: 'edit',
        Cell: ({ row }) => {
          const integrity = row.original;

          if (
            !integrity &&
            (!integrity.integrityType || !integrity.integrityRuleType)
          ) {
            return null;
          }

          return (
            <TableRowActionsCell minItems={1}>
              {row.original?.fileLink ? (
                <IconButton
                  color="default"
                  onClick={() => {
                    setIsLoading(true);
                    handlePreviewAttachment(row.original);
                  }}
                >
                  <PreviewIcon />
                </IconButton>
              ) : (
                <Tooltip title="Link unavailable or broken">
                  <span>
                    <IconButton color="default" disabled>
                      <PreviewIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {row.original?.fileLink ? (
                <IconButton
                  color="default"
                  onClick={() =>
                    window.open(
                      row.original.fileLink,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  <EditIcon />
                </IconButton>
              ) : (
                <Tooltip title="Link unavailable.">
                  <span>
                    <IconButton color="default" disabled>
                      <LinkOffIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton
                title="Delete"
                onClick={() => {
                  handleDelete(row);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </TableRowActionsCell>
          );
        },
      },
    ],
    [handleDelete, handlePreviewAttachment, setIsLoading],
  );

  return (
    <Table
      useExpanded
      subrowOffset={3}
      items={tableItems}
      columns={columns}
      useGlobalFilter={true}
      noItemsMessage="Rule not compliant Document(s) missing"
    />
  );
};

const styles = (theme) => ({
  root: {
    backgroundColor: theme.palette.grey[700],
  },
});

export default withStyles(styles)(IntegrityAttachmentsTable);
